import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { AudioTrackingService } from './audio-tracking.service';

export interface AudioTrackingConfig {
  podcastTitle: string;
  companyTicker: string;
  researchDate: string;
  companySlug: string;
}

@Directive({
  selector: '[appAudioTracking]',
  standalone: true,
})
export class AudioTrackingDirective implements OnInit, OnDestroy {
  @Input() appAudioTracking!: AudioTrackingConfig;
  @Output() playEvent = new EventEmitter<{ currentTime: number }>();
  @Output() pauseEvent = new EventEmitter<{
    currentTime: number;
    sessionPlayTime: number;
    totalPlayTime: number;
  }>();
  @Output() endedEvent = new EventEmitter<{ totalPlayTime: number }>();
  @Output() timeUpdateEvent = new EventEmitter<{
    currentTime: number;
    duration: number;
  }>();

  private audioElement: HTMLAudioElement;

  constructor(
    private el: ElementRef<HTMLAudioElement>,
    private audioTrackingService: AudioTrackingService
  ) {
    this.audioElement = this.el.nativeElement;
  }

  ngOnInit() {
    this.setupEventListeners();
  }

  private setupEventListeners() {
    this.audioElement.addEventListener('play', this.handlePlay.bind(this));
    this.audioElement.addEventListener('pause', this.handlePause.bind(this));
    this.audioElement.addEventListener('ended', this.handleEnded.bind(this));
    this.audioElement.addEventListener('timeupdate', this.handleTimeUpdate.bind(this));
  }

  private handlePlay() {
    const currentTime = this.audioElement.currentTime;
    this.audioTrackingService.onPlay(currentTime);
    this.playEvent.emit({ currentTime });
  }

  private handlePause() {
    const currentTime = this.audioElement.currentTime;
    const stats = this.audioTrackingService.onPause(currentTime);
    this.pauseEvent.emit({
      currentTime,
      sessionPlayTime: stats.sessionPlayTime,
      totalPlayTime: stats.totalPlayTime,
    });
  }

  private handleEnded() {
    const currentTime = this.audioElement.currentTime;
    const stats = this.audioTrackingService.onEnded(currentTime);
    this.endedEvent.emit({ totalPlayTime: stats.totalPlayTime });
  }

  private handleTimeUpdate() {
    const currentTime = this.audioElement.currentTime;
    const duration = this.audioElement.duration;
    this.audioTrackingService.onTimeUpdate(currentTime, duration);
    this.timeUpdateEvent.emit({ currentTime, duration });
  }

  ngOnDestroy() {
    // Send final stats if still playing
    if (this.audioElement && !this.audioElement.paused) {
      const currentTime = this.audioElement.currentTime;
      const stats = this.audioTrackingService.getSessionStats(currentTime);
      // Emit session end event
      this.pauseEvent.emit({
        currentTime,
        sessionPlayTime: stats.sessionPlayTime,
        totalPlayTime: stats.totalPlayTime,
      });
    }

    // Reset the audio tracking service
    this.audioTrackingService.reset();

    // Remove event listeners
    this.audioElement.removeEventListener('play', this.handlePlay.bind(this));
    this.audioElement.removeEventListener('pause', this.handlePause.bind(this));
    this.audioElement.removeEventListener('ended', this.handleEnded.bind(this));
    this.audioElement.removeEventListener('timeupdate', this.handleTimeUpdate.bind(this));
  }
}
